import type { StudioWithAccess } from "../api/types";
import { useMemo, type ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { SharedSetup } from "../../../../../../shared/frontend/onboarding/SharedSetup";
import { createBillingSetupAdapter } from "../../../../../../shared/frontend/onboarding/createBillingSetupAdapter";
import { createM1MemberBillingAdapter } from "../billing/createM1MemberBillingAdapter";
import { M1MemberAddressEditorShell, createM1MemberAddressBookAdapter, createM1MemberAddressListRenderer } from "../billing/m1memberAddressBook";
import { getM1MemberBillingMatrix } from "../billing/m1memberBillingMatrix";
import { useAuth } from "../auth/useAuth";
import { useI18n } from "../i18n/useI18n";
import { Permissions } from "../constants/permissions";
import { setupGateEnabled } from "./setupGate";
import { PLATFORM_ADMIN_ROLE } from "../constants/authRole";
import { ROUTE_BILLING } from "../constants/appRoutes";
export function MemberSetup({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const { text, lang } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const canConfigure = auth.hasPermission(Permissions.MasterDataWrite);
  const canManage = auth.hasPermission(Permissions.BillingWrite);
  const adapter = useMemo(() => createBillingSetupAdapter({
    adapter: { ...createM1MemberBillingAdapter({ api: auth.api, tr: text }), addressBook: createM1MemberAddressBookAdapter(auth.api) },
    tr: text, locale: lang, addressEditorShell: M1MemberAddressEditorShell,
    addressListRenderer: createM1MemberAddressListRenderer(text), matrixCategories: getM1MemberBillingMatrix(text),
  }, { canManageContract: canManage, loadSteps: async () => {
    if (!canConfigure) return [];
    const studios = await auth.api.get<StudioWithAccess[]>("/api/v1/studios/with-access");
    return [{ id: "studio", title: text("Studios"), state: studios.some(studio => studio.canAccess) ? "done" : "pending", required: false,
      open: () => navigate("/settings/master-data?tab=studios") }];
  } }), [auth.api, canManage, canConfigure, lang, navigate, text]);
  return <SharedSetup adapter={adapter} scope={`${auth.info?.accountId}:${auth.info?.tenantId}`} locale={lang}
    brand={{ name: text("m1member"), logo: `${import.meta.env.BASE_URL ?? ""}images/mq-64x35.png` }}
    onLogout={() => { auth.logout(); navigate("/login"); }}
    enabled={setupGateEnabled && canManage && auth.info?.role !== PLATFORM_ADMIN_ROLE && !auth.info?.tenantImportCommitInProgress}
    suspended={location.pathname === ROUTE_BILLING} homePath="/overview" billingPath="/billing" refreshToken={location.pathname}>
    {children}
  </SharedSetup>;
}
